import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {useCallback} from "react";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Choice, RadioGroup} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelLarge,
} from "@khanacademy/wonder-blocks-typography";
import {ValidatedFormTextarea} from "@repo/component-validated-form-textarea";
import {
    useRateInteraction,
    useSetAIGuideInteractionReaction,
} from "@repo/guide-core";
import {
    FeedbackType,
    type FeedbackDropDown,
    type InteractionFeedback,
    type WrittenFeedback,
} from "@repo/guide-types";
import {Errors, KAError} from "@repo/logging";
import * as MobileApp from "@repo/mobile-app-integration";
import {staticUrl} from "@repo/urls";

import {useSupportImageInput} from "./image-attach/use-support-image-input.ts";
import {useUserMayInputImages} from "./image-attach/use-user-may-input-images.ts";

const StyledImg = addStyle("img");

const MAX_SELECT_WIDTH = 350;

interface Props {
    disabledWrittenFeedbackType?: WrittenFeedback["type"];
    threadId: string;
    interactionId: string;
    onClose: () => void;
}

const StyledForm = addStyle("form");

const FeedbackForm = ({
    disabledWrittenFeedbackType,
    threadId,
    interactionId,
    onClose,
}: Props): React.ReactElement => {
    const supportImageInput = useSupportImageInput();
    const userMayInputImages = useUserMayInputImages();

    const rateInteraction = useRateInteraction();
    const [loading, setLoading] = React.useState(false);
    const [writtenFeedbackType, setWrittenFeedbackType] = React.useState(
        FeedbackType.Negative,
    );
    const [feedbackRating, setFeedbackRating] = React.useState<
        string | null | undefined
    >(null);
    const [feedbackIssueType, setFeedbackIssueType] =
        React.useState<FeedbackDropDown>("OTHER_ISSUE");
    const [note, setNote] = React.useState("");
    const [rewrittenResponse, setRewrittenResponse] = React.useState("");
    const setAIGuideInteractionMutation = useSetAIGuideInteractionReaction();
    // Reset writtenFeedbackType if current choice is disabled.
    if (disabledWrittenFeedbackType === writtenFeedbackType) {
        setWrittenFeedbackType(
            writtenFeedbackType === FeedbackType.Positive
                ? FeedbackType.Negative
                : FeedbackType.Positive,
        );
    }

    const submitFeedback = useCallback(async () => {
        let numFeedbackRating: number | null = null;
        let interactionFeedback: InteractionFeedback;

        switch (writtenFeedbackType) {
            case FeedbackType.General:
                // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null'. | TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
                numFeedbackRating = parseInt(feedbackRating);
                interactionFeedback = {
                    type: FeedbackType.General,
                    positive: true,
                    note,
                    rating: numFeedbackRating,
                };
                rateInteraction(interactionId, interactionFeedback);
                break;
            case FeedbackType.Negative:
                interactionFeedback = {
                    type: FeedbackType.Negative,
                    note,
                    rewrittenResponse,
                    positive: false,
                    issue: feedbackIssueType,
                };
                rateInteraction(interactionId, interactionFeedback);
                break;
            case FeedbackType.Positive:
                interactionFeedback = {
                    type: FeedbackType.Positive,
                    note,
                    rewrittenResponse,
                    positive: true,
                };
                rateInteraction(interactionId, interactionFeedback);
                break;
            default:
                // This will catch if more feedback types are added but we
                // forget to update this code.
                throw new KAError("Unsupported Feedback type", Errors.Internal);
        }

        // TODO (GL-60) Need to handle the error case with some reasonable UI.
        await setAIGuideInteractionMutation({
            threadId,
            interactionId,
            note,
            rewrittenResponse,
            rating: numFeedbackRating,
        });

        onClose();
    }, [
        feedbackRating,
        threadId,
        interactionId,
        note,
        onClose,
        rateInteraction,
        rewrittenResponse,
        setAIGuideInteractionMutation,
        writtenFeedbackType,
        feedbackIssueType,
    ]);

    const handleSubmitFeedback = useCallback(() => {
        setLoading(true);
        submitFeedback();
    }, [submitFeedback]);

    const handleWrittenFeedbackTypeChange = (
        writtenFeedbackTypeString: string,
    ) => {
        const writtenFeedbackType = writtenFeedbackTypeString as FeedbackType;
        setWrittenFeedbackType(writtenFeedbackType);
    };

    const canSubmit =
        // User can submit feedback of a General type iff they include a note.
        (writtenFeedbackType === FeedbackType.General &&
            note.trim().length > 0) ||
        // User can submit other feedback if an issueType has been chosen.
        writtenFeedbackType !== FeedbackType.General;

    // Participate in the mobile app UX stack so that the app is updated
    // and the app can close/submit this modal.
    MobileApp.Guide.useUXStack("feedback-form", {
        onCancel: onClose,
        canConfirm: canSubmit,
        onConfirm: handleSubmitFeedback,
        translatedTitle: t`Leave feedback`,
    });

    const generalFeedbackRatings: Record<string, string> = {
        "1": t`Very bad`,
        "2": t`Bad`,
        "3": t`Neutral `,
        "4": t`Good`,
        "5": t`Very good`,
    };

    const negativeFeedbackIssueTypes: Record<FeedbackDropDown, string> = {
        IMAGE_ISSUE: t`Image Issue`,
        LANGUAGE_ISSUE: t`Language Issue`,
        MATH_ISSUE: t`Math Issue`,
        TECHNICAL_ISSUE: t`Technical Issue`,
        OTHER_ISSUE: t`Other`,
    };

    // We only want to show the feedback issue if image input is supported
    // (EG the flag is on, the activity supports it) and the user has access.
    if (!userMayInputImages || !supportImageInput) {
        // @ts-expect-error - TS2790: The operand of a 'delete' operator must be optional.
        delete negativeFeedbackIssueTypes.IMAGE_ISSUE;
    }

    return (
        <StyledForm
            style={styles.form}
            data-testid="feedback-form"
            name="feedback-form"
            aria-label={t`Leave feedback`}
        >
            <View style={styles.fieldsContainer}>
                <View style={styles.noFlexShrink}>
                    <Strut size={spacing.small_12} />
                    <HeadingSmall>{t`Leave feedback`}</HeadingSmall>
                    <Strut size={spacing.medium_16} />
                </View>
                <RadioGroup
                    groupName="writtenFeedbackType"
                    onChange={handleWrittenFeedbackTypeChange}
                    selectedValue={writtenFeedbackType as string}
                    style={styles.noFlexShrink}
                >
                    <Choice
                        value={FeedbackType.Negative as string}
                        label={t`There was an issue`}
                        disabled={
                            disabledWrittenFeedbackType ===
                            FeedbackType.Negative
                        }
                    />
                    <Choice
                        value={FeedbackType.Positive as string}
                        label={t`The response was good`}
                        disabled={
                            disabledWrittenFeedbackType ===
                            FeedbackType.Positive
                        }
                    />
                    <Choice
                        value={FeedbackType.General as string}
                        label={t`General feedback`}
                    />
                </RadioGroup>

                {/* If negative feedback is selected, then display issue-type dropdown */}
                {writtenFeedbackType === FeedbackType.Negative && (
                    <SingleSelect
                        style={styles.select}
                        selectedValue={String(feedbackIssueType)}
                        onChange={(issue) => {
                            setFeedbackIssueType(issue as FeedbackDropDown);
                        }}
                        placeholder={t`Select one (required)`}
                        testId="feedback-form-dropdown"
                        aria-label={t`Select one (required)`}
                    >
                        {Object.entries(negativeFeedbackIssueTypes).map(
                            ([key, value]) => (
                                <OptionItem
                                    key={value}
                                    label={value}
                                    value={key}
                                />
                            ),
                        )}
                    </SingleSelect>
                )}

                {/* Only allow specifying a scaled rating for the general writtenFeedbackType */}
                {writtenFeedbackType === FeedbackType.General && (
                    <SingleSelect
                        style={styles.select}
                        selectedValue={String(feedbackRating)}
                        onChange={(rating) => setFeedbackRating(rating)}
                        placeholder={t`Please rate your Khanmigo experience`}
                        testId="feedback-form-dropdown"
                        aria-label={t`Please rate your Khanmigo experience`}
                    >
                        {Object.entries(generalFeedbackRatings).map(
                            ([key, value]) => (
                                <OptionItem
                                    key={value}
                                    label={value}
                                    value={key}
                                />
                            ),
                        )}
                    </SingleSelect>
                )}

                <ValidatedFormTextarea
                    labelIsSrOnly={true}
                    label={
                        writtenFeedbackType === FeedbackType.General
                            ? t`Type message (do NOT share any personal data)`
                            : t`Please describe the issue (optional) - do NOT share any personal data`
                    }
                    rows={2}
                    disabled={loading}
                    value={note}
                    onChange={({target: {value}}) => setNote(value)}
                    errorMessage=""
                    required={false}
                    placeholder={
                        writtenFeedbackType === FeedbackType.General
                            ? t`Type message (do NOT share any personal data)`
                            : t`Please describe the issue (optional) - do NOT share any personal data`
                    }
                    style={styles.textArea}
                    testId="feedback-textarea"
                />

                {/* Only collect rewritten feedback for "non-general" writtenFeedbackType */}
                {writtenFeedbackType !== FeedbackType.General && (
                    <ValidatedFormTextarea
                        labelIsSrOnly={true}
                        label={t`What response would be better (optional) - do NOT share any personal data`}
                        rows={2}
                        disabled={loading}
                        value={rewrittenResponse}
                        onChange={({target: {value}}) =>
                            setRewrittenResponse(value)
                        }
                        errorMessage=""
                        required={false}
                        placeholder={t`What response would be better (optional) - do NOT share any personal data`}
                        style={styles.textArea}
                        testId="rewritten-response-textarea"
                    />
                )}
                <Strut size={spacing.large_24} />
                <View style={styles.helpAndSupport}>
                    <View style={styles.helpAndSupportHeading}>
                        <StyledImg
                            alt=""
                            style={styles.guideImage}
                            src={staticUrl(
                                "/images/guide/khanmigo-blushing.svg",
                            )}
                        />
                        <Strut size={spacing.xSmall_8} />
                        <LabelLarge>{t`Khanmigo Help & Support`}</LabelLarge>
                    </View>
                    <Strut size={spacing.xxSmall_6} />
                    <Body>
                        <Trans>
                            If you have questions about how to use Khanmigo,
                            please visit our{" "}
                            <Link
                                href={t`https://support.khanacademy.org/hc/categories/24449383583757`}
                                target="_blank"
                            >
                                FAQ & Resources Center
                            </Link>
                        </Trans>
                    </Body>
                </View>
            </View>
            <View style={styles.buttonGroup}>
                <Button
                    size="small"
                    kind="secondary"
                    disabled={loading}
                    onClick={onClose}
                    testId="cancel-button"
                >
                    {t`Cancel`}
                </Button>
                <Strut size={spacing.medium_16} />
                <Button
                    size="small"
                    spinner={loading}
                    disabled={!canSubmit}
                    onClick={handleSubmitFeedback}
                    testId="submit-button"
                >
                    {t`Submit`}
                </Button>
            </View>
        </StyledForm>
    );
};

const styles = {
    form: {
        height: "100%",
        justifyContent: "space-between",
        backgroundColor: color.white,
        padding: "8px 24px",
        borderRadius: 8,
    },
    fieldsContainer: {
        overflowY: "auto",
    },
    buttonGroup: {
        paddingTop: spacing.small_12,
        paddingBottom: spacing.small_12,
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    select: {
        flexShrink: 0,
        minWidth: "100%",
        maxWidth: MAX_SELECT_WIDTH,
        paddingTop: spacing.medium_16,
    },
    textArea: {
        flexShrink: 0,
        fontSize: 14,
        borderRadius: 8,
    },
    noFlexShrink: {
        flexShrink: 0,
    },
    helpAndSupport: {flexShrink: 0},
    helpAndSupportHeading: {
        flexDirection: "row",
        alignItems: "center",
    },
    guideImage: {
        width: 24,
    },
} as const;

export {FeedbackForm};
